/**
 * Supabase API layer for profile operations
 */

import { supabase } from '@/integrations/supabase/client';
import { createSuccessResponse, createErrorResponse } from '../errors';
import type { ApiResponse } from '../types/common';
import type { UpdateProfileRequest } from '../types/requests';
import type { Profile } from '../types/responses';

/**
 * Fetch user profile by user ID
 */
export async function fetchProfile(userId: string): Promise<ApiResponse<Profile>> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      return createErrorResponse(error);
    }

    return createSuccessResponse(data as Profile);
  } catch (error) {
    return createErrorResponse(error);
  }
}

/**
 * Update user profile
 */
export async function updateProfile(
  userId: string,
  updates: UpdateProfileRequest
): Promise<ApiResponse<Profile>> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      return createErrorResponse(error);
    }

    return createSuccessResponse(data as Profile);
  } catch (error) {
    return createErrorResponse(error);
  }
}

/**
 * Upload avatar image
 */
export async function uploadAvatar(
  userId: string,
  file: File
): Promise<ApiResponse<{ avatarUrl: string }>> {
  try {
    const fileExt = file.name.split('.').pop();
    // Use userId as folder name for RLS policy compatibility
    const filePath = `${userId}/avatar.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, file, { upsert: true });

    if (uploadError) {
      return createErrorResponse(uploadError);
    }

    const { data: { publicUrl } } = supabase.storage
      .from('avatars')
      .getPublicUrl(filePath);

    const { error: updateError } = await supabase
      .from('profiles')
      .update({ avatar_url: publicUrl })
      .eq('id', userId);

    if (updateError) {
      return createErrorResponse(updateError);
    }

    return createSuccessResponse({ avatarUrl: publicUrl });
  } catch (error) {
    return createErrorResponse(error);
  }
}
